import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Grid, makeStyles, TextField, Typography } from '@material-ui/core';

import useBenchmarkSearch, {
  SET_SERIES_TYPE_ID,
  SET_SERIES_TYPE_DETAIL_ID,
  SET_PERIODICITY_ID,
  SET_IS_ACTIVE,
  SET_PARENT_OBJECT_TYPE_ID,
  SET_SEARCH_CRITERIA
} from 'hooks/useBenchmarkSearch';
import CheckboxButton from 'components/buttons/CheckboxButton';
import LoadingSpinner from 'components/LoadingSpinner';
import { ThemeButton, Message } from 'components/shared';

import AdvancedSearchDropdown from './AdvancedSearchDropdown';
import LOOKUP_TYPES from '../utils/dashboardLookupTypes';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: '5px'
  },
  field: {
    margin: '0'
  },

  input: {
    padding: '0 4px',
    color: theme.palette.grey.dark,
    border: `1px solid ${theme.palette.grey.medium}`,
    borderRadius: '4px',
    marginBottom: '8px',
    '&:before': {
      display: 'none'
    },
    '&:after': {
      display: 'none'
    },
    '&:hover': {
      borderColor: theme.palette.accent.main,
      color: theme.palette.accent.main
    },
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.light
    }
  },
  searchPanel: {
    flexGrow: 1,
    padding: '0 8px'
  },
  searchField: {
    flexGrow: '1'
  },
  searchInput: {
    padding: '0 4px',
    color: theme.palette.grey.dark,
    border: `1px solid ${theme.palette.grey.medium}`,
    borderRadius: '4px',
    '&:before': {
      display: 'none'
    },
    '&:after': {
      display: 'none'
    },
    '&:hover': {
      borderColor: theme.palette.accent.main,
      color: theme.palette.accent.main
    },
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.light
    }
  },
  results: {
    ...theme.scrollStyles,
    overflow: 'auto',
    marginTop: '5px',
    height: '135px',
    border: `1px solid ${theme.palette.grey.medium}`,
    borderRadius: '4px'
  },
  resultPlaceholder: {
    color: theme.palette.grey.dark,
    fontSize: '0.8rem',
    fontStyle: 'italic'
  },
  resultItem: {
    display: 'flex',
    color: theme.palette.grey.dark,
    padding: '3px 0',
    alignItems: 'center',
    cursor: 'pointer',
    '& p': {
      fontSize: '0.8rem'
    }
  },
  highlighted: {
    backgroundColor: theme.palette.primary.light,
    '& p': {
      color: theme.palette.primary.main
    }
  },
  cancelButton: {
    width: '100%',
    color: theme.palette.primary.main
  },
  acceptButton: {
    width: '100%',
    '&:disabled': {
      backgroundColor: theme.palette.primary.disabled,
      color: theme.palette.white
    }
  }
}));

const advancedActionsMappingObject = {
  searchCriteria: SET_SEARCH_CRITERIA,
  seriesTypeId: SET_SERIES_TYPE_ID,
  seriesTypeDetailId: SET_SERIES_TYPE_DETAIL_ID,
  periodicityId: SET_PERIODICITY_ID,
  parentObjectTypeId: SET_PARENT_OBJECT_TYPE_ID
};

// TODO: Improve code quality
const AdvancedDashboardSearch = ({ onAccepted, clearAdvancedView, index }) => {
  const classes = useStyles();
  const [state, dispatch, search, results, apiState] = useBenchmarkSearch();
  const [highlightedIndex, setHighlightedIndex] = useState();

  const acceptBenchmark = () => {
    const item = results[highlightedIndex];
    if (index) {
      onAccepted(item, index);
    } else {
      onAccepted(item);
    }

    clearAdvancedView();
  };

  const highlightItem = resultIndex => {
    setHighlightedIndex(resultIndex);
  };

  const handleInputChange = (e, id) => {
    const fieldId = id || e.target.id;

    dispatch({ type: advancedActionsMappingObject[fieldId], payload: e.target.value });
  };

  const onIsActiveChange = e => {
    dispatch({ type: SET_IS_ACTIVE, payload: e.value });
  };

  const onKeyPress = e => {
    if (e.key === 'Enter') {
      search();
    }
  };

  const getResultClasses = resultIndex => {
    const classList = ['resultItem'];
    if (highlightedIndex === resultIndex) {
      classList.push('highlighted');
    }
    return classList.map(c => classes[c]).join(' ');
  };

  const renderResults = () => {
    if (apiState.isLoading) {
      return <LoadingSpinner />;
    }
    if (apiState.isError && apiState.error) {
      return <Message type="error" text={apiState.error} />;
    }
    if (!results.length) {
      return <Typography className={classes.resultPlaceholder}>No search results</Typography>;
    }

    return results.map((benchmark, resultIndex) => (
      <Box
        key={benchmark.timeSerieId}
        onClick={() => highlightItem(resultIndex)}
        className={getResultClasses(resultIndex)}
      >
        <Typography>{benchmark.name}</Typography>
      </Box>
    ));
  };

  return (
    <Box className={classes.searchPanel}>
      <Grid container direction="row" spacing={2}>
        <Grid item xs={10}>
          <TextField
            id="searchCriteria"
            className={classes.searchField}
            value={state.searchCriteria}
            onKeyPress={onKeyPress}
            onChange={e => handleInputChange(e, 'searchCriteria')}
            InputProps={{ className: classes.searchInput }}
            fullWidth
          />
        </Grid>
        <Grid item xs={2}>
          <ThemeButton themeColor="accent" size="small" onClick={search} fullWidth>
            Search
          </ThemeButton>
        </Grid>
      </Grid>

      <Box className={classes.root}>
        <Grid container direction="row" alignItems="center" spacing={1}>
          {LOOKUP_TYPES &&
            LOOKUP_TYPES.length > 0 &&
            LOOKUP_TYPES.map(dropdown => (
              <AdvancedSearchDropdown
                key={dropdown.id}
                id={dropdown.id}
                type={dropdown.type}
                label={dropdown.label}
                value={state[dropdown.id]}
                onChange={e => handleInputChange(e, dropdown.id)}
                width={3}
              />
            ))}
          <Grid item xs="auto">
            <CheckboxButton
              hasTransparentBg
              title="Is Active"
              checked={state.isActive}
              onChange={onIsActiveChange}
            />
          </Grid>
        </Grid>
      </Box>

      <Grid
        container
        direction="row"
        justify="flex-start"
        alignItems="flex-start"
        spacing={1}
        className={classes.subSection}
      >
        <Grid className={classes.results} item xs={12}>
          {renderResults()}
        </Grid>
        <Grid item xs="auto">
          <Button
            disableElevation
            variant="contained"
            color="primary"
            className={classes.acceptButton}
            disabled={highlightedIndex === undefined}
            onClick={acceptBenchmark}
          >
            Accept
          </Button>
        </Grid>
        <Grid item xs="auto">
          <Button
            color="primary"
            variant="outlined"
            className={classes.cancelButton}
            onClick={clearAdvancedView}
          >
            Cancel
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

AdvancedDashboardSearch.propTypes = {
  onAccepted: PropTypes.func.isRequired,
  clearAdvancedView: PropTypes.func.isRequired,
  index: PropTypes.number
};
AdvancedDashboardSearch.defaultProps = {
  index: null
};
export default AdvancedDashboardSearch;
